import api from "./api";
import {
  ok, findRecord, isoUtc, nameOf, dayStartIso, dayEndIso, fileToBase64, evidenceMime, downloadFrom, reasonOf, extensionTag, REASON_MAX,
} from "./core";

const rid = async (cycleId, employeeId) => (await findRecord(cycleId, employeeId)).performance_record_id;

export async function setEligibility(cycleId, employeeId, status, reason) {
  return api.post(`/records/${await rid(cycleId, employeeId)}/eligibility`, { eligibility_status: status, reason: reasonOf(reason, "Eligibility updated") });
}

export async function changeReviewer(cycleId, employeeId, reviewerId, reason) {
  return api.post(`/records/${await rid(cycleId, employeeId)}/reviewer`, { reviewer_employee_id: reviewerId, reason: reasonOf(reason, "Reviewer reassigned") });
}

export async function overrideDeadline(cycleId, employeeId, { stage, opensOn, closesOn, reason }) {
  if (!opensOn || !closesOn) throw new Error("Pick both the opening and the closing date.");
  if (closesOn < opensOn) throw new Error("The closing date must be on or after the opening date.");
  const opensAt = dayStartIso(opensOn);
  const closesAt = dayEndIso(closesOn);
  const tag = extensionTag(stage, opensAt, closesAt);
  const text = reasonOf(reason, "Deadline extended").slice(0, REASON_MAX - tag.length - 1);
  return api.post(`/records/${await rid(cycleId, employeeId)}/deadline-overrides`, {
    stage_type: stage,
    opens_at: opensAt,
    closes_at: closesAt,
    reason: `${text} ${tag}`,
  });
}

export async function raiseException(cycleId, employeeId, { code, severity, description, reason }) {
  return api.post(`/records/${await rid(cycleId, employeeId)}/exceptions`, {
    exception_code: code,
    severity,
    description,
    reason: reasonOf(reason, description),
  });
}

export const resolveException = (exceptionId, status, reason) =>
  api.post(`/exceptions/${exceptionId}/resolve`, { status, reason: reasonOf(reason, status === "WAIVED" ? "Waived" : "Resolved") });

export async function remindRecord(cycleId, employeeId, reason) {
  return api.post(`/records/${await rid(cycleId, employeeId)}/remind`, { reason: reasonOf(reason, "Reminder sent") });
}
export const remindRecordById = (recordId, reason) => api.post(`/records/${recordId}/remind`, { reason: reasonOf(reason, "Reminder sent") });

const CONTEXT_LABEL = { GOAL: "Goal", KRA: "KRA", KPI: "KPI", SELF_ASSESSMENT: "Self assessment", MANAGER_ASSESSMENT: "Manager assessment" };

export async function listEvidence(owner, ownerId) {
  const { data } = await api.get(`/evidence/${owner}/${ownerId}`);
  return ok(data.map((a) => ({
    id: owner === "pip" ? a.pip_attachment_id : a.attachment_id,
    owner,
    name: a.original_filename,
    title: a.title || "",
    description: a.description || "",
    mimeType: a.mime_type,
    size: a.size_bytes,
    scanStatus: a.scan_status,
    context: CONTEXT_LABEL[a.evidence_context] || a.evidence_context || "Plan evidence",
    uploadedAt: isoUtc(a.uploaded_at),
    uploadedBy: a.uploaded_by_employee_id ? nameOf(a.uploaded_by_employee_id) : "",
  })));
}

export async function uploadEvidence(owner, ownerId, file, { itemId, context = "SELF_ASSESSMENT", reason } = {}) {
  const mime = evidenceMime(file);
  if (!mime) throw new Error("Evidence must be a PDF, PNG, JPEG or plain-text file.");
  const body = {
    filename: file.name,
    mime_type: mime,
    content_base64: await fileToBase64(file),
    reason: reasonOf(reason, "Evidence uploaded"),
  };
  if (owner === "performance") {
    body.context = context;
    if (itemId) body.item_id = Number(itemId);
  }
  return api.post(`/evidence/${owner}/${ownerId}`, body);
}

export const downloadEvidence = (owner, attachmentId, name) => downloadFrom(`/attachments/${owner}/${attachmentId}`, name);

export const scanEvidence = (owner, attachmentId, status, reason) =>
  api.post(`/attachments/${owner}/${attachmentId}/scan`, { status, reason: reasonOf(reason, `Scan result ${status.toLowerCase()}`) });
